<template>
    <div class="container admin-table-page">
        <!-- Fila del título y flecha hacia atrás -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- Fila de filtros debajo del título a la izquierda -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <b-form-select
                        id="report-select"
                        v-model="fecha"
                        class="filter-select"
                        :disabled="isLoading"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectReport }}
                        </b-form-select-option>
                        <option 
                            v-for="item in lstFechaCierre" 
                            :key="item?.id" 
                            :value="item?.fecha_cierre"
                        >
                            {{ item?.fecha_cierre }}
                        </option>
                    </b-form-select>                  
                    <b-button
                        class="consult-btn ml-2"
                        @click="consulReportPorContrato()"
                        :disabled="!fecha || isLoading"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>
            </div>
        </div>

        <div class="admin-table-block mt-4">
            <div v-if="!isLoading">
                <CorporateDataTable
                    :items="lstReporContrato"
                    :fields="allFields"
                    :labels="tableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100, 500]"
                    :narrow="false"
                    :table-min-height="500"
                    :table-max-height="500"
                    :show-search="showSearchTable"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="tableFilterDefs"
                    :toolbar-disabled="!showToolbarExtras"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-excel-btn"
                                @click="genReportePorContrato()"
                                :disabled="!showToolbarExtras || lstReporContrato.length === 0"
                            >
                                <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-1" />
                            </b-button>
                        </div>
                    </template>

                    <template #cell(total_valor_canon)="data">
                        <div class="text-right">{{ formatNumber(data.item?.total_valor_canon) }}</div> 
                    </template>
                    <template #cell(pasivo_inicial)="data">
                        <div class="text-right">{{ formatNumber(data.item?.pasivo_inicial) }}</div> 
                    </template>
                    <template #cell(total_cambio_pasivo)="data">
                        <div class="text-right">{{ formatNumber(data.item?.total_cambio_pasivo) }}</div> 
                    </template>
                    <template #cell(total_pago_capital)="data">
                        <div class="text-right">{{ formatNumber(data.item?.total_pago_capital) }}</div> 
                    </template>
                    <template #cell(total_intereses)="data">
                        <div class="text-right">{{ formatNumber(data.item?.total_intereses) }}</div> 
                    </template>
                    <template #cell(pasivo_final)="data">
                        <div class="text-right">{{ formatNumber(data.item?.pasivo_final) }}</div> 
                    </template>
                    <template #cell(activo_derecho_uso_bruto)="data">
                        <div class="text-right">{{ formatNumber(data.item?.activo_derecho_uso_bruto) }}</div> 
                    </template>
                    <template #cell(total_gasto_amortizacion)="data">
                        <div class="text-right">{{ formatNumber(data.item?.total_gasto_amortizacion) }}</div> 
                    </template>
                    <template #cell(amortizacion_acumulada)="data">
                        <div class="text-right">{{ formatNumber(data.item?.amortizacion_acumulada) }}</div> 
                    </template>
                    <template #cell(valor_baja)="data">
                        <div class="text-right">{{ formatNumber(data.item?.valor_baja) }}</div> 
                    </template>
                    <template #cell(estado)="data">
                        <div class="text-left">{{ data.item.estado == 'Inicial' ? 'Vigente' : data.item.estado }}</div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- Spinner de carga -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import ContratoCierre from '../../services/ContratoCierre'; 
import Reportes from '../../services/Reportes'; 
import Authentication from "../../services/Authentication"; 
import CorporateDataTable from '../utils/CorporateDataTable.vue';
import { Workbook } from 'exceljs';

export default {
    name: 'ReportePorContrato',
    components: { CorporateDataTable },    
    data: () => ({
        fecha: '',
        isLoading: false,
        showToolbarExtras: false,
        lstFechaCierre: [],
        lstReporContrato: [],
        nameCompania: '',
    }),    
    computed: {
        labels() {
            const isSpanish = this.$store.state._language === 'es-ES';
            const labels = {
                es: {
                    title: 'Reporte por contrato',
                    consultar: 'Consultar',
                    selectReport: 'Seleccione Fecha',
                    loading: 'Cargando...',
                    noData: 'No hay datos disponibles',
                    noMatches: 'No se encontraron resultados',
                    searchPlaceholder: 'Buscar en la tabla...',
                    rowsPerPage: 'Filas por página:',
                    noSort: 'Sin orden',
                    sortAsc: 'A-Z / Menor a mayor',
                    sortDesc: 'Z-A / Mayor a menor',
                    columns: 'Columnas',
                    showAllColumns: 'Mostrar todas',
                    filters: 'Filtros',
                    clearFilters: 'Limpiar filtros',
                    nit: 'Nit',
                    nombre: 'Arrendador',
                    contrato: 'Contrato',
                    totalValorCanon: 'Total valor canon',
                    pasivoInicial: 'Pasivo inicial',
                    totalCambioPasivo: 'Total cambio pasivo',
                    totalPagoCapital: 'Total pago capital',
                    totalIntereses: 'Total intereses',
                    pasivoFinal: 'Pasivo final',
                    activoDerechoUsoBruto: 'Activo derecho uso bruto',
                    totalGastoAmortizacion: 'Total gasto amortización',
                    amortizacionAcumulada: 'Amortización acumulada',
                    valorBaja: 'Baja de Activos',
                    estado: 'Estado',
                    fechaEstado: 'Fecha finalización',
                    filterNit: 'Filtrar Nit...',
                    filterNombre: 'Filtrar arrendador...',
                    filterContrato: 'Filtrar contrato...',
                    filterTotalValorCanon: 'Filtrar valor canon...',
                    filterPasivoInicial: 'Filtrar pasivo inicial...',
                    filterTotalCambioPasivo: 'Filtrar cambio pasivo...',
                    filterTotalPagoCapital: 'Filtrar pago capital...',
                    filterTotalIntereses: 'Filtrar intereses...',
                    filterPasivoFinal: 'Filtrar pasivo final...',
                    filterActivoDerechoUsoBruto: 'Filtrar activo derecho uso...',
                    filterTotalGastoAmortizacion: 'Filtrar gasto amortización...',
                    filterAmortizacionAcumulada: 'Filtrar amortización acumulada...',
                    filterValorBaja: 'Filtrar baja de activos...',
                    filterEstado: 'Filtrar estado...',
                },
                en: {
                    title: 'Contract report',
                    consultar: 'Consult',
                    selectReport: 'Select Date',
                    loading: 'Loading...',
                    noData: 'No data available',
                    noMatches: 'No results found',
                    searchPlaceholder: 'Search in table...',
                    rowsPerPage: 'Rows per page:',
                    noSort: 'No sorting',
                    sortAsc: 'A-Z / Low to high',
                    sortDesc: 'Z-A / High to low',
                    columns: 'Columns',
                    showAllColumns: 'Show all',
                    filters: 'Filters',
                    clearFilters: 'Clear filters',
                    nit: 'Nit',
                    nombre: 'Lessor',
                    contrato: 'Contract',
                    totalValorCanon: 'Total canon value',
                    pasivoInicial: 'Initial liabilities',
                    totalCambioPasivo: 'Total passive change',
                    totalPagoCapital: 'Total capital payment',
                    totalIntereses: 'Total interests',
                    pasivoFinal: 'Final passive',
                    activoDerechoUsoBruto: 'Asset right gross use',
                    totalGastoAmortizacion: 'Total amortization expense',
                    amortizacionAcumulada: 'Accumulated amortization',
                    valorBaja: 'Asset disposal',
                    estado: 'State',
                    fechaEstado: 'Completion date',
                    filterNit: 'Filter Nit...',
                    filterNombre: 'Filter lessor...',
                    filterContrato: 'Filter contract...',
                    filterTotalValorCanon: 'Filter canon value...',
                    filterPasivoInicial: 'Filter initial liabilities...',
                    filterTotalCambioPasivo: 'Filter passive change...',
                    filterTotalPagoCapital: 'Filter capital payment...',
                    filterTotalIntereses: 'Filter interests...',
                    filterPasivoFinal: 'Filter final passive...',
                    filterActivoDerechoUsoBruto: 'Filter asset right use...',
                    filterTotalGastoAmortizacion: 'Filter amortization expense...',
                    filterAmortizacionAcumulada: 'Filter accumulated amortization...',
                    filterValorBaja: 'Filter asset disposal...',
                    filterEstado: 'Filter by status...',
                }
            };
            return labels[isSpanish ? 'es' : 'en'];
        },
        
        allFields() {
            return [
                { key: 'identificacion', label: this.labels.nit, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '120px' } },
                { key: 'nombre', label: this.labels.nombre, sortable: true, thClass: 'text-left', tdClass: 'text-start align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '400px' } },
                { key: 'nro_contrato', label: this.labels.contrato, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '100px' } },
                { key: 'total_valor_canon', label: this.labels.totalValorCanon, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
                { key: 'pasivo_inicial', label: this.labels.pasivoInicial, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
                { key: 'total_cambio_pasivo', label: this.labels.totalCambioPasivo, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
                { key: 'total_pago_capital', label: this.labels.totalPagoCapital, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
                { key: 'total_intereses', label: this.labels.totalIntereses, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
                { key: 'pasivo_final', label: this.labels.pasivoFinal, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
                { key: 'activo_derecho_uso_bruto', label: this.labels.activoDerechoUsoBruto, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '180px' } },
                { key: 'total_gasto_amortizacion', label: this.labels.totalGastoAmortizacion, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '180px' } },
                { key: 'amortizacion_acumulada', label: this.labels.amortizacionAcumulada, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '180px' } },
                { key: 'valor_baja', label: this.labels.valorBaja, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
                { key: 'estado', label: this.labels.estado, sortable: true, thClass: 'text-center', tdClass: 'text-start align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
                { key: 'fecha_estado', label: this.labels.fechaEstado, sortable: true, thClass: 'text-center', tdClass: 'text-start align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
            ];
        },
        
        tableFilterDefs() {
            return [
                { key: 'identificacion', type: 'text', placeholder: this.labels.filterNit },
                { key: 'nombre', type: 'text', placeholder: this.labels.filterNombre },
                { key: 'nro_contrato', type: 'text', placeholder: this.labels.filterContrato },
                { key: 'total_valor_canon', type: 'text', placeholder: this.labels.filterTotalValorCanon },
                { key: 'pasivo_inicial', type: 'text', placeholder: this.labels.filterPasivoInicial },
                { key: 'total_cambio_pasivo', type: 'text', placeholder: this.labels.filterTotalCambioPasivo },
                { key: 'total_pago_capital', type: 'text', placeholder: this.labels.filterTotalPagoCapital },
                { key: 'total_intereses', type: 'text', placeholder: this.labels.filterTotalIntereses },
                { key: 'pasivo_final', type: 'text', placeholder: this.labels.filterPasivoFinal },
                { key: 'activo_derecho_uso_bruto', type: 'text', placeholder: this.labels.filterActivoDerechoUsoBruto },
                { key: 'total_gasto_amortizacion', type: 'text', placeholder: this.labels.filterTotalGastoAmortizacion },
                { key: 'amortizacion_acumulada', type: 'text', placeholder: this.labels.filterAmortizacionAcumulada },
                { key: 'valor_baja', type: 'text', placeholder: this.labels.filterValorBaja },
                { key: 'estado', type: 'text', placeholder: this.labels.filterEstado },
                { key: 'fecha_estado', type: 'text', placeholder: this.labels.filterFechaEstado },
            ];
        },
        
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            };
        },
        
        showSearchTable() {
            return true;
        }
    },
    
    methods: {
        goBack() {
            this.$router.back();
        },
        
        formatNumber(value) {
            if (value == null) return '0';
            return new Intl.NumberFormat('es-CO', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value);
        },
        
        async consulReportPorContrato() {
            if (!this.fecha) {
                this.$toast.error(this.$store.state._language === 'es-ES'
                    ? 'Diligenciar la fecha.'
                    : 'Please select a date.');
                return;
            }

            this.isLoading = true;

            try {
                const res = await Reportes.generaReportePorContrato(this.fecha);
                this.lstReporContrato = res.data.lstReporContrato || [];
                this.showToolbarExtras = this.lstReporContrato.length > 0;

                if (!this.lstReporContrato.length) {
                    this.$toast.warning(this.$store.state._language === 'es-ES'
                        ? 'No hay datos para la fecha seleccionada.'
                        : 'No data available for the selected date.');
                }
            } catch (error) {
                console.error('Error en consulReportPorContrato:', error);
                const message = error.response?.data?.message || 
                    (this.$store.state._language === 'es-ES'
                        ? 'Error al consultar los datos.'
                        : 'Error fetching data.');
                this.$toast.error(message);
                this.lstReporContrato = [];
                this.showToolbarExtras = false;
            } finally {
                this.isLoading = false;
            }
        },
        
        async genReportePorContrato() {
            if (!this.lstReporContrato.length) return;

            try {
                const workbook = new Workbook();
                const worksheet = workbook.addWorksheet(`Reporte por contrato ${this.fecha}`);
                
                const headers = [
                    'Nit', 'Nombre', 'Contrato', 'Total valor canon',
                    'Pasivo inicial', 'Total cambio pasivo', 'Total pago capital',
                    'Total intereses', 'Pasivo final', 'Activo derecho uso bruto',
                    'Total gasto amortización', 'Amortización acumulada', 'Baja de Activos',
                    'Estado', 'Fecha finalización'
                ];

                // Título
                worksheet.addRow([`Reporte por contrato - ${this.fecha}`]);
                worksheet.mergeCells('A1:M1');
                worksheet.getCell('A1').font = { name: 'Arial', bold: true, size: 12 };
                worksheet.getCell('A1').alignment = { horizontal: 'center' };

                // Información de generación
                worksheet.addRow(['']);
                worksheet.mergeCells('A2:O2');
                const cell2 = worksheet.getCell('A2');
                cell2.value = {
                    richText: [
                        { text: 'Generado por: ', font: { bold: true } },
                        { text: `${this.$store.state.userName} | ` },
                        { text: 'Fecha generación: ', font: { bold: true } },
                        { text: `${this.getFechaActual()} | ` },
                        { text: 'Compañía: ', font: { bold: true } },
                        { text: `${this.nameCompania}` },
                    ]
                };
                cell2.font = { name: 'Arial', size: 12 };
                cell2.alignment = { horizontal: 'center', vertical: 'middle' };

                worksheet.addRow(['']);
                worksheet.mergeCells('A3:M3');

                // Headers
                const headerRow = worksheet.addRow(headers);
                headerRow.font = { name: 'Arial', bold: true, size: 12 };
                headerRow.alignment = { horizontal: 'center' };

                // Datos
                this.lstReporContrato.forEach(item => {
                    worksheet.addRow([
                        item.identificacion,
                        item.nombre,
                        item.nro_contrato,
                        this.formatNumber(item.total_valor_canon),
                        this.formatNumber(item.pasivo_inicial),
                        this.formatNumber(item.total_cambio_pasivo),
                        this.formatNumber(item.total_pago_capital),
                        this.formatNumber(item.total_intereses),
                        this.formatNumber(item.pasivo_final),
                        this.formatNumber(item.activo_derecho_uso_bruto),
                        this.formatNumber(item.total_gasto_amortizacion),
                        this.formatNumber(item.amortizacion_acumulada),
                        this.formatNumber(item.valor_baja),
                        item.estado == 'Inicial' ? 'Vigente' : item.estado,
                        item.fecha_estado
                    ]);
                });

                // Aplicar bordes
                const border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                };

                for (let row = 4; row <= worksheet.rowCount; row++) {
                    for (let col = 1; col <= worksheet.columnCount; col++) {
                        const cell = worksheet.getCell(row, col);
                        cell.border = border;
                        cell.alignment = { horizontal: 'center' };
                    }
                }

                // Ajustar ancho de columnas
                worksheet.columns.forEach((column, index) => {
                    const headerLength = headers[index]?.length || 10;
                    column.width = Math.max(headerLength, 15);
                });

                // Generar y descargar archivo
                const buffer = await workbook.xlsx.writeBuffer();
                const blob = new Blob([buffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `Reporte por contrato ${this.fecha}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);

            } catch (err) {
                console.error('Error generando Excel:', err);
                this.$toast.error(this.$store.state._language === 'es-ES'
                    ? 'Error al generar el archivo Excel.'
                    : 'Error generating Excel file.');
            }
        },
        
        getFechaActual() {
            return new Date().toISOString().split('T')[0];
        },

        async loadInitialData() {
            try {
                // Cargar fechas
                const fechasRes = await ContratoCierre.getListadoFechas();
                this.lstFechaCierre = fechasRes.data.lstFechaCierre || [];

                // Cargar parámetros
                const paramsRes = await Authentication.leaParametros();
                this.nameCompania = paramsRes.data.parametros.compania || '';

            } catch (err) {
                console.error('Error cargando datos iniciales:', err);
                this.$toast.error(this.$store.state._language === 'es-ES'
                    ? 'Error al cargar datos iniciales.'
                    : 'Error loading initial data.');
            }
        }
    },
    
    watch: {
        fecha(val) {
            if (!val) {
                this.lstReporContrato = [];
                this.showToolbarExtras = false;
            }
        }
    },
    
    mounted() {
        this.loadInitialData();
    }
}
</script>

<style scoped>
/* Estilos optimizados */
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
    transition: opacity 0.2s;
}

.niif-header .consult-btn:hover:not(:disabled) {
    background-color: #5a6268 !important;
    border-color: #545b62 !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.niif-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-excel-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #016690;
    color: white;
    border: 1px solid #085170;
    transition: background-color 0.2s, opacity 0.2s;
}

.table-toolbar-excel-btn:hover:not(:disabled) {
    background-color: #085170;
    border-color: #0e3a4d;
}

.table-toolbar-excel-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* Responsive */
@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        justify-content: flex-start;
        gap: 6px;
    }

    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }

    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }

    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }

    .niif-header .filters-container {
        gap: 4px;
    }

    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }
}
</style>